import {useEffect, useState} from "react";
import {useSearchParams} from "react-router-dom";
import {useQueryClient} from "@tanstack/react-query";
import {BrandImage} from "../../../components/brand-image";
import {StyledHeader, StyledHeaderWrapper} from "../../../components/navbar/navbar.style";
import {RecoveryCodesModal} from "../../../components/settings/two-factor/RecoveryCodesModal";
import {TotpSetupDrawer} from "../../../components/settings/two-factor/TotpSetupDrawer";
import {TwoFactorSetupModal} from "../../../components/settings/two-factor/TwoFactorSetupModal";
import {LEGACY_PROFILE_QUERY_KEY} from "../../../features/profile/hooks/useProfile";
import {useResolvePostAuthRedirect} from "../hooks/use-resolve-post-auth-redirect";
import {
  StyledPageWrapper,
  StyledContentWrapper,
  StyledCard,
  StyledIconCircle,
  StyledHeading,
  StyledDescription,
  StyledButtonGroup,
  StyledActionButton,
} from "./two-factor-nudge.style";

type TVisibleModal = "none" | "setup" | "authenticator-app" | "backup-codes";

const TwoFactorNudgePage = () => {
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get("redirectTo");
  const {resolvePostAuthRedirect, isKycStatusLoading} = useResolvePostAuthRedirect();
  const queryClient = useQueryClient();

  const [currentVisibleModal, setCurrentVisibleModal] = useState<TVisibleModal>("none");
  const [totpData, setTotpData] = useState<{qrUri: string; secret: string} | null>(null);
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [redirectPending, setRedirectPending] = useState(false);

  // Defers the actual navigation until the KYC status query has resolved —
  // calling resolvePostAuthRedirect while it's still loading would default
  // isKycVerified to false and bounce an already-verified coach to KYC.
  useEffect(() => {
    if (redirectPending && !isKycStatusLoading) {
      setRedirectPending(false);
      resolvePostAuthRedirect(redirectTo);
    }
  }, [redirectPending, isKycStatusLoading, resolvePostAuthRedirect, redirectTo]);

  const handleEnableTwoFactor = () => {
    setCurrentVisibleModal("setup");
  };

  return (
    <StyledPageWrapper>
      <StyledHeaderWrapper>
        <StyledHeader>
          <BrandImage variant="dark" />
        </StyledHeader>
      </StyledHeaderWrapper>
      <StyledContentWrapper>
        <StyledCard>
          <StyledIconCircle>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
              <path
                d="M12 2L4 5v6c0 5.25 3.4 9.74 8 11 4.6-1.26 8-5.75 8-11V5l-8-3z"
                fill="#52C41A"
              />
              <path
                d="M9 12.5l2 2 4-4.2"
                stroke="#fff"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </StyledIconCircle>
          <div>
            <StyledHeading>Add an extra layer of security</StyledHeading>
            <StyledDescription>
              Enable two-factor authentication to protect your account from unauthorised
              access. Takes less than a minute.
            </StyledDescription>
          </div>
          <StyledButtonGroup>
            <StyledActionButton type="primary" onClick={handleEnableTwoFactor}>
              Enable 2-factor authentication
            </StyledActionButton>
            <StyledActionButton onClick={() => setRedirectPending(true)}>
              Maybe later
            </StyledActionButton>
          </StyledButtonGroup>
        </StyledCard>
      </StyledContentWrapper>
      <TwoFactorSetupModal
        visible={currentVisibleModal === "setup"}
        onClose={() => setCurrentVisibleModal("none")}
        onTotpInitiated={(data) => {
          setTotpData(data);
          setCurrentVisibleModal("authenticator-app");
        }}
        onEmailSetupSuccess={() => {
          setCurrentVisibleModal("none");
          setRedirectPending(true);
        }}
      />
      <TotpSetupDrawer
        visible={currentVisibleModal === "authenticator-app"}
        qrUri={totpData?.qrUri}
        secret={totpData?.secret}
        onClose={() => setCurrentVisibleModal("none")}
        onSuccess={(codes) => {
          setBackupCodes(codes);
          setCurrentVisibleModal("backup-codes");
        }}
      />
      <RecoveryCodesModal
        visible={currentVisibleModal === "backup-codes"}
        codes={backupCodes}
        onClose={() => {
          setCurrentVisibleModal("none");
          queryClient.invalidateQueries({queryKey: [LEGACY_PROFILE_QUERY_KEY]});
          setRedirectPending(true);
        }}
      />
    </StyledPageWrapper>
  );
};

export default TwoFactorNudgePage;
